import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Download,
  ExternalLink,
  Info,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { AnalysisAccountSelect } from '@/components/analysis/AnalysisAccountSelect';
import {
  ANALYSIS_ACCOUNT_MODE_PARAM,
  NEW_ANALYSIS_ACCOUNT_VALUE,
  isManualReportAnalysisProfileId,
  isNewAnalysisAccountMode,
} from '@/components/analysis/analysisAccountMode';
import { TokenChips } from '@/components/analysis/TokenChips';
import {
  apiErrorMessage,
  CONNECTIONS_PATH,
  formatWhen,
  statusVariant,
} from '@/components/analysis/reportAnalysisUi';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { SyncReportsButton } from '@/components/SyncReportsButton';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useAuth } from '@/contexts/AuthContext';
import { useReportAnalysis } from '@/hooks/useReportAnalysis';
import { adsApi } from '@/api/client';
import {
  extractAccountRows,
  uniqueAccountRows,
  type LinkedAccountRow,
} from '@/api/managerAccounts';
import { ANALYSIS_GRAIN_LABELS, normalizeAnalysisConfig } from '@/types/reportAnalysis';

const QUERY_ORDER = Object.keys(ANALYSIS_GRAIN_LABELS);
const PROFILE_PARAM = 'profileId';

type SelectableAccount = LinkedAccountRow & { profileId: string };

function ConnectToConnectionsButton({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link to={CONNECTIONS_PATH} className={className}>
      {children}
    </Link>
  );
}

export function GlobalAnalysisPage() {
  const { user, canAccessProfile } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const isDemo = Boolean(user?.is_demo);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedProfileId = searchParams.get(PROFILE_PARAM)?.trim() ?? '';
  const wantsNewAccount = isNewAnalysisAccountMode(searchParams);

  const accountsQuery = useQuery({
    queryKey: ['manager-accounts', user?.id],
    queryFn: async () => {
      const res = await adsApi.getManagerAccounts();
      if (!res.success) throw new Error('Failed to load accounts');
      return res.data;
    },
    enabled: Boolean(user?.id),
  });

  const manualAccountsQuery = useQuery({
    queryKey: ['manual-analysis-accounts', user?.id],
    queryFn: async () => {
      const res = await adsApi.listManualAnalysisAccounts();
      if (!res.success) throw new Error(res.message || 'Failed to load manual accounts');
      return (res.data ?? []).map((row) =>
        normalizeAnalysisConfig(row as unknown as Record<string, unknown>, row.profileId)
      );
    },
    enabled: Boolean(user?.id),
  });

  const accounts = useMemo<SelectableAccount[]>(() => {
    const rows = uniqueAccountRows(extractAccountRows(accountsQuery.data)).filter(
      (row): row is SelectableAccount => {
        const profileId = row.profileId?.trim();
        return Boolean(profileId) && canAccessProfile(profileId as string);
      }
    );
    for (const manual of manualAccountsQuery.data ?? []) {
      const profileId = manual.profileId?.trim();
      if (!profileId || rows.some((row) => row.profileId === profileId)) continue;
      rows.push({
        profileId,
        accountName: manual.accountName.trim() || 'Untitled account',
      });
    }
    return rows;
  }, [accountsQuery.data, canAccessProfile, manualAccountsQuery.data]);

  const selectedProfileId = useMemo(() => {
    if (wantsNewAccount) return '';
    if (requestedProfileId && accounts.some((row) => row.profileId === requestedProfileId)) {
      return requestedProfileId;
    }
    if (
      requestedProfileId &&
      isManualReportAnalysisProfileId(requestedProfileId) &&
      (manualAccountsQuery.data ?? []).some((row) => row.profileId === requestedProfileId)
    ) {
      return requestedProfileId;
    }
    return accounts[0]?.profileId ?? '';
  }, [accounts, wantsNewAccount, requestedProfileId, manualAccountsQuery.data]);

  const profileId = selectedProfileId;
  const isManualProfile = isManualReportAnalysisProfileId(profileId);
  const usesZipUpload = isManualProfile;
  const showManualNameField = isManualProfile;

  useEffect(() => {
    if (wantsNewAccount) return;
    if (!selectedProfileId) return;
    if (searchParams.get(PROFILE_PARAM) === selectedProfileId) return;
    const next = new URLSearchParams(searchParams);
    next.set(PROFILE_PARAM, selectedProfileId);
    setSearchParams(next, { replace: true });
  }, [selectedProfileId, wantsNewAccount, searchParams, setSearchParams]);

  const createManualMutation = useMutation({
    mutationFn: async () => {
      const res = await adsApi.createManualAnalysisAccount();
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to create account');
      return normalizeAnalysisConfig(
        res.data as unknown as Record<string, unknown>,
        res.data.profileId
      );
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['manual-analysis-accounts', user?.id] });
      queryClient.setQueryData(['report-analysis-config', created.profileId], created);
      const next = new URLSearchParams();
      next.set(PROFILE_PARAM, created.profileId);
      setSearchParams(next);
    },
    onError: (error) => {
      showToast(apiErrorMessage(error, 'Failed to create manual account'), 'error');
      const next = new URLSearchParams(searchParams);
      next.delete(ANALYSIS_ACCOUNT_MODE_PARAM);
      setSearchParams(next);
    },
  });

  useEffect(() => {
    if (!wantsNewAccount || createManualMutation.isPending || createManualMutation.isSuccess) return;
    createManualMutation.mutate();
    // Intentionally only when the user chooses "Add new account".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsNewAccount]);

  const analysis = useReportAnalysis(profileId, {
    skipInventory: isManualProfile || !profileId,
  });
  const {
    startMonth,
    setStartMonth,
    endMonth,
    setEndMonth,
    brandNames,
    setBrandNames,
    competitorNames,
    setCompetitorNames,
    accountName,
    setAccountName,
    configLoading,
    windowError,
    inventoryQuery,
    jobsQuery,
    startMutation,
    uploadMutation,
    retryMutation,
    download,
    fileRef,
    zipName,
    setZipName,
    cells,
    months,
    byMonth,
    jobs,
    busy,
  } = analysis;

  const onAccountChange = (nextValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextValue === NEW_ANALYSIS_ACCOUNT_VALUE) {
      createManualMutation.reset();
      next.set(ANALYSIS_ACCOUNT_MODE_PARAM, 'new');
      next.delete(PROFILE_PARAM);
      setSearchParams(next);
      return;
    }
    next.delete(ANALYSIS_ACCOUNT_MODE_PARAM);
    if (nextValue) next.set(PROFILE_PARAM, nextValue);
    else next.delete(PROFILE_PARAM);
    setSearchParams(next);
  };

  const canGenerate =
    Boolean(selectedProfileId) && !isManualProfile && !windowError && !busy && !configLoading;
  const canUploadZip =
    !busy &&
    !configLoading &&
    !windowError &&
    isManualProfile &&
    Boolean(profileId) &&
    Boolean(accountName.trim());

  return (
    <div className="min-h-screen bg-page">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex min-w-0 items-center gap-4">
            <ProductBrand variant="light" showTagline={false} />
            <div className="hidden items-center gap-2 border-l border-border pl-4 sm:flex">
              <Building2 className="h-5 w-5 shrink-0 text-brand" aria-hidden />
              <Link
                to="/accounts"
                className="text-headline-md font-semibold text-foreground hover:text-brand"
              >
                Advertising accounts
              </Link>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 sm:gap-4">
            <Link
              to={CONNECTIONS_PATH}
              className="text-body-sm font-medium text-brand hover:underline"
            >
              Manage connections
            </Link>
            {!isDemo ? (
              <ConnectToConnectionsButton className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-brand-ink px-3.5 text-body-md font-medium text-brand-foreground hover:bg-chrome-border">
                <Link2 className="h-3.5 w-3.5" aria-hidden />
                Connect Amazon
              </ConnectToConnectionsButton>
            ) : null}
            <Link to="/dashboard" className="text-body-sm text-foreground-muted hover:text-foreground">
              Dashboard
            </Link>
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <div>
          <h1 className="text-headline-md font-semibold text-foreground">Analysis</h1>
          <p className="mt-1 text-sm text-foreground-muted">
            Get audits for your connected Amazon accounts and choose the date range.
          </p>
        </div>

        <div className="flex flex-col gap-4 rounded-xl border border-brand/20 bg-brand-subtle px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface text-brand shadow-sm">
              <Link2 className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-foreground">Connect an advertising account</p>
              <p className="mt-0.5 text-sm text-foreground-muted">
                Don&apos;t see your account? You can connect a new Amazon or Flipkart Ads account on
                the Connections page or add it manually using a console report.
              </p>
            </div>
          </div>
          <ConnectToConnectionsButton className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 text-body-md font-medium text-foreground hover:border-brand">
            <ExternalLink className="h-4 w-4" aria-hidden />
            Connect account
          </ConnectToConnectionsButton>
        </div>

        {accountsQuery.isLoading ? (
          <Card className="flex items-center justify-center p-10">
            <Spinner className="h-8 w-8" />
          </Card>
        ) : accountsQuery.isError ? (
          <Card className="p-6 text-sm text-error-ink">
            {apiErrorMessage(accountsQuery.error, 'Could not load advertising accounts')}
          </Card>
        ) : (
          <>
            <Card className="space-y-5 p-6">
              <h2 className="text-body-md font-semibold text-foreground">Analysis settings</h2>

              {wantsNewAccount || createManualMutation.isPending ? (
                <p className="text-sm text-foreground-muted">Creating manual account…</p>
              ) : (
                <>
              <div
                className={
                  showManualNameField
                    ? 'grid gap-4 md:grid-cols-2 lg:grid-cols-4'
                    : 'grid gap-4 md:grid-cols-2 lg:grid-cols-3'
                }
              >
                <div className={showManualNameField ? 'lg:col-span-1' : 'md:col-span-2 lg:col-span-1'}>
                  <AnalysisAccountSelect
                    accounts={accounts}
                    value={selectedProfileId}
                    onChange={onAccountChange}
                  />
                </div>
                {showManualNameField ? (
                  <div>
                    <label
                      htmlFor="manual-account-name"
                      className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle"
                    >
                      Account name <span className="text-error-ink">*</span>
                    </label>
                    <Input
                      id="manual-account-name"
                      required
                      value={accountName}
                      onChange={(event) => setAccountName(event.target.value)}
                      placeholder="Enter account name (e.g. My Amazon Account)"
                      disabled={configLoading}
                    />
                  </div>
                ) : null}
                <div>
                  <label
                    htmlFor="analysis-start-month"
                    className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle"
                  >
                    Start month
                  </label>
                  <Input
                    id="analysis-start-month"
                    type="month"
                    value={startMonth}
                    onChange={(event) => setStartMonth(event.target.value)}
                  />
                </div>
                <div>
                  <label
                    htmlFor="analysis-end-month"
                    className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle"
                  >
                    End month
                  </label>
                  <Input
                    id="analysis-end-month"
                    type="month"
                    value={endMonth}
                    onChange={(event) => setEndMonth(event.target.value)}
                  />
                </div>
              </div>
              {windowError ? (
                <p className="text-sm text-error-ink">{windowError}</p>
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <TokenChips
                  key={`${profileId}-brands`}
                  label="Brand names"
                  tokens={brandNames}
                  onChange={setBrandNames}
                  placeholder="e.g. your brand, product line — Enter to add"
                  disabled={configLoading}
                />
                <TokenChips
                  key={`${profileId}-competitors`}
                  label="Competitor names"
                  tokens={competitorNames}
                  onChange={setCompetitorNames}
                  placeholder="e.g. rival brands — Enter to add"
                  disabled={configLoading}
                />
              </div>

              {!usesZipUpload ? (
              <div>
                <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="text-body-md font-semibold text-foreground">S3 coverage</h2>
                    <p className="mt-0.5 text-sm text-foreground-muted">
                      Found data in your archived reports for the selected period.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <SyncReportsButton
                      profileId={profileId}
                      className="inline-flex h-8 items-center rounded-lg border border-border px-3 text-body-sm font-medium text-foreground hover:border-brand"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void inventoryQuery.refetch()}
                      disabled={!profileId || inventoryQuery.isFetching}
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${inventoryQuery.isFetching ? 'animate-spin' : ''}`}
                      />
                      Refresh
                    </Button>
                  </div>
                </div>
                {!profileId ? (
                  <p className="text-sm text-foreground-muted">
                    Select a connected advertising account to see archived report coverage.
                  </p>
                ) : inventoryQuery.isLoading ? (
                  <p className="text-sm text-foreground-muted">Checking archives…</p>
                ) : inventoryQuery.error ? (
                  <p className="text-sm text-error-ink">
                    {apiErrorMessage(inventoryQuery.error, 'Could not list S3 archives')}
                  </p>
                ) : cells.length === 0 ? (
                  <p className="text-sm text-foreground-muted">
                    No archives listed for this window. Sync reports first, then wait for gzip files
                    to land on S3.
                  </p>
                ) : (
                  <TableWrap>
                    <Table compact>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          {QUERY_ORDER.map((queryId) => (
                            <TableHead key={queryId}>{ANALYSIS_GRAIN_LABELS[queryId]}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <tbody>
                        {months.map((month) => {
                          const row = byMonth.get(month) ?? [];
                          return (
                            <TableRow key={month}>
                              <TableCell className="font-medium">{month}</TableCell>
                              {QUERY_ORDER.map((queryId) => {
                                const cell = row.find((item) => item.queryId === queryId);
                                const count = cell?.objectCount ?? 0;
                                return (
                                  <TableCell key={queryId}>
                                    <Badge variant={count > 0 ? 'success' : 'warning'}>
                                      {count > 0
                                        ? `${count} object${count === 1 ? '' : 's'}`
                                        : 'Missing on S3'}
                                    </Badge>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </div>
              ) : (
                <p className="text-sm text-foreground-muted">
                  There are no archived reports for a manual account. Upload a Console CSV zip to
                  generate the analysis.
                </p>
              )}

              <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  disabled={usesZipUpload ? !canUploadZip : !canGenerate}
                  onClick={() => {
                    if (usesZipUpload) {
                      fileRef.current?.click();
                      return;
                    }
                    startMutation.mutate();
                  }}
                >
                  {startMutation.isPending || (usesZipUpload && uploadMutation.isPending) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Generate analysis
                </Button>
                <div className="flex flex-col items-stretch gap-2 sm:items-end">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".zip,application/zip"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file) return;
                        setZipName(file.name);
                        uploadMutation.mutate(file, {
                          onSuccess: () => {
                            void queryClient.invalidateQueries({
                              queryKey: ['manual-analysis-accounts', user?.id],
                            });
                          },
                        });
                      }}
                    />
                    <Button
                      variant="secondary"
                      disabled={!canUploadZip}
                      onClick={() => fileRef.current?.click()}
                    >
                      {uploadMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {zipName && uploadMutation.isPending ? zipName : 'Upload Console CSV zip'}
                    </Button>
                  </div>
                  {usesZipUpload ? (
                    <p className="flex items-start gap-1.5 text-sm text-foreground-muted sm:max-w-xs sm:text-right">
                      <Info
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground-subtle sm:hidden"
                        aria-hidden
                      />
                      Upload reports of the last 4 months.
                    </p>
                  ) : null}
                </div>
              </div>
                </>
              )}
            </Card>

            <Card className="p-6">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-body-md font-semibold text-foreground">Job history</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void jobsQuery.refetch()}
                  disabled={!profileId}
                >
                  <RefreshCw className={`h-4 w-4 ${jobsQuery.isFetching ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              {!profileId ? (
                <p className="text-sm text-foreground-muted">
                  Select an advertising account to see diagnosis jobs.
                </p>
              ) : jobsQuery.isLoading ? (
                <p className="text-sm text-foreground-muted">Loading jobs…</p>
              ) : jobs.length === 0 ? (
                <p className="text-sm text-foreground-muted">
                  {usesZipUpload
                    ? 'No diagnosis jobs yet. Upload a Console CSV zip to start analysis for this account.'
                    : 'No diagnosis jobs yet for this account.'}
                </p>
              ) : (
                <TableWrap>
                  <Table compact>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Files</TableHead>
                      </TableRow>
                    </TableHeader>
                    <tbody>
                      {jobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell>
                            <div className="font-medium">
                              {job.startMonth} – {job.endMonth}
                            </div>
                            <div className="text-body-sm text-foreground-muted">
                              {job.source === 's3_upload' ? 'Console CSV upload' : 'S3 archives'}
                            </div>
                            {job.error ? (
                              <div className="mt-1 text-body-sm text-error-ink">{job.error}</div>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge variant={statusVariant(job.emailStatus)}>
                                {job.emailStatus || '—'}
                              </Badge>
                              {job.status === 'succeeded' && job.emailStatus === 'failed' ? (
                                <Button
                                  variant="link"
                                  size="sm"
                                  disabled={retryMutation.isPending}
                                  onClick={() => retryMutation.mutate(job.id)}
                                >
                                  Retry email
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-foreground-muted">
                            {formatWhen(job.createdAt)}
                          </TableCell>
                          <TableCell>
                            {job.status === 'succeeded' ? (
                              <div className="flex flex-wrap gap-2">
                                {job.xlsxReady ? (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => void download(job, 'xlsx')}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    Excel
                                  </Button>
                                ) : null}
                                {job.pdfReady ? (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => void download(job, 'pdf')}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    PDF
                                  </Button>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-foreground-muted">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
